/**
 * Top-left panel: mission clock, garrison alert pips, the detection glyph, the objective line, the
 * standing orders, the mission's own timer (10 §3) and the extraction instruction under it.
 */

import './objectivePanel.css';
import type { BindingsPort } from '../../input/bindings';
import { TICK_RATE } from '../../sim/types';
import type { MissionDef, World } from '../../sim/types';
import { DetectionGlyph } from './detectionGlyph';
import { clamp, el, mmss, show, Shown } from './dom';
import { alertCountdownLine, timerLine } from './missionRules';
import { standingLine } from './missionSignal';
import { OrdersView } from './ordersView';
import { type HudTextSize, ordersWidth } from './textSize';
import { keyHint } from './weaponSlots';

/** The line under the objective, indexed by the code from `subLineCode`; `{recall}` is the bound key. */
const SUB_LINES: ReadonlyArray<{ text: string; warn: boolean } | null> = [
  null,
  { text: 'RECALL THE OSPREY [{recall}]', warn: false },
  { text: 'REACH THE LADDER', warn: false },
  { text: 'BOARDING', warn: false },
  { text: 'STRANDED — WALK OUT SOUTH', warn: true },
  { text: 'RECALL THE OSPREY [{recall}] — LEAD THEM TO THE X', warn: false },
  { text: 'NOTHING MORE TO DO HERE — RECALL THE OSPREY [{recall}]', warn: true },
];

/**
 * Index into `SUB_LINES`: what the player should be doing about extraction right now.
 *
 * An escort mission is only complete when someone has boarded, which needs the Osprey: so the
 * recall line must come up as soon as anyone is following him, not when the objective is done.
 */
export function subLineCode(world: World, complete: boolean): number {
  const status = world.ranger.status;
  if (status === 'boarding') return 3;
  if (status !== 'active' && status !== 'downed') return 0;
  const phase = world.osprey.phase;
  if (world.stranded || phase === 'departed') return 4;
  if (phase === 'inbound' || phase === 'hovering') return 2;
  if (world.osprey.recalled) return 0;
  // A lost objective scores nothing more: the only thing left is to get the ranger home.
  if (world.objective.failed) return 6;
  if (complete) return 1;
  return world.objective.captives.some((c) => c.state === 'following') ? 5 : 0;
}

export class ObjectivePanel {
  readonly el: HTMLElement;
  private readonly clock: HTMLElement;
  private readonly pipRow: HTMLElement;
  private readonly pips: HTMLElement[];
  private readonly glyph = new DetectionGlyph();
  private readonly objective: HTMLElement;
  private readonly objectiveCount: HTMLElement;
  private readonly sub: HTMLElement;
  private readonly orders: OrdersView;
  /** The mission's own clock: a timed event pending, a zone being held, a quest item on its way. */
  private readonly timer: HTMLElement;
  private readonly shown = new Shown();

  constructor(private readonly def: MissionDef, private readonly bindings?: BindingsPort) {
    this.el = el('div', 'hud-panel hud-tl');
    this.clock = el('span', 'hud-clock', '--:--');
    this.pipRow = el('span', 'hud-pips');
    this.pips = [el('span', 'hud-pip'), el('span', 'hud-pip'), el('span', 'hud-pip')];
    this.pipRow.append(...this.pips);
    const top = el('div', 'hud-row');
    top.append(this.clock, this.pipRow, this.glyph.el);

    this.objective = el('div', 'hud-objective');
    this.objectiveCount = el('span', 'hud-objective-count');
    this.objective.append(
      el('span', 'hud-check'),
      el('span', 'hud-objective-text', def.objective.label),
      this.objectiveCount,
    );
    this.sub = el('div', 'hud-sub hud-hidden');
    // The orders come in full, then fold to the one rule that can lose this mission (ordersView.ts). The
    // timer and the extraction line keep their own rows underneath, so neither can hide the Recall hint.
    this.orders = new OrdersView(def, keyHint(bindings?.actions() ?? null, 'map', 'TAB'));
    this.timer = el('div', 'hud-timer hud-hidden');
    this.el.append(top, this.objective, this.orders.el, this.timer, this.sub);
  }

  /** The map key is held: show the whole orders block for as long as it is down. */
  setOrdersHeld(held: boolean): void {
    this.orders.setHeld(held);
  }

  setTextSize(size: HudTextSize): void {
    this.orders.setTextSize(size);
    this.timer.style.maxWidth = `${ordersWidth(size, true)}px`;
    this.sub.style.maxWidth = `${ordersWidth(size, true)}px`;
  }

  /** The bindings may have changed while the game was paused: the orders hint names the key again. */
  refreshKeys(): void {
    this.orders.setKey(keyHint(this.bindings?.actions() ?? null, 'map', 'TAB'));
  }

  update(world: World): void {
    const shown = this.shown;
    const seconds = Math.max(0, Math.ceil(world.clockTicks / TICK_RATE));
    if (shown.changed('clock', seconds)) this.clock.textContent = mmss(seconds, true);
    if (shown.changed('clockLow', seconds < 60)) this.clock.classList.toggle('hud-clock-low', seconds < 60);

    const alert = clamp(Math.round(world.alert), 0, 3);
    if (shown.changed('alert', alert)) {
      this.pips.forEach((pip, i) => pip.classList.toggle('hud-pip-on', i < alert));
      this.pipRow.classList.toggle('hud-pips-alarm', alert >= 3);
    }

    this.glyph.update(world.enemies);

    const destroyed = world.targetsDestroyed;
    const total = world.targetsTotal;
    const failed = world.objective.failed;
    const complete = world.objective.complete && !failed;
    if (shown.changed('objective', destroyed * 1000 + total + (failed ? 0.5 : 0) + (complete ? 0.25 : 0))) {
      this.objectiveCount.textContent = failed ? 'FAILED' : `[${destroyed}/${total}]`;
      this.objective.classList.toggle('hud-objective-done', complete);
      this.objective.classList.toggle('hud-objective-failed', failed);
      // A losing rule that has fired still shouts: the reason takes the whole block and never folds away.
      if (failed) this.orders.fail(world.objective.failReason ?? 'OBJECTIVE FAILED');
    }
    this.orders.update(world);

    // The mission's own timer has a line to itself, so it can never hide "RECALL THE OSPREY".
    const timer = timerLine(this.def, world);
    if (shown.changed('timer', timer ?? '')) {
      show(this.timer, timer !== null);
      this.timer.textContent = timer ?? '';
    }

    // A consequence counting down outranks everything else on this line.
    const countdown = failed ? null : alertCountdownLine(this.def, world);
    if (countdown !== null) {
      if (shown.changed('sub', countdown)) {
        show(this.sub, true);
        this.sub.textContent = countdown;
        this.sub.classList.add('hud-sub-warn');
      }
      return;
    }
    // A mission judged by the clock says what he should be doing about it, until the Osprey is coming.
    const standing = failed || world.osprey.phase !== 'away' ? null : standingLine(this.def, world);
    if (standing !== null) {
      if (shown.changed('sub', standing.text)) {
        show(this.sub, true);
        this.sub.textContent = standing.text;
        this.sub.classList.toggle('hud-sub-warn', standing.warn);
      }
      return;
    }
    const code = subLineCode(world, complete);
    if (shown.changed('sub', code)) {
      const line = SUB_LINES[code] ?? null;
      show(this.sub, line !== null);
      if (line !== null) {
        const recall = keyHint(this.bindings?.actions() ?? null, 'recall', 'G');
        this.sub.textContent = line.text.replace('{recall}', recall === '' ? '-' : recall);
        this.sub.classList.toggle('hud-sub-warn', line.warn);
      }
    }
  }
}
